<script lang="ts">
  import Tabs from '../../../../components/tabs/Tabs.svelte'
  import TabsList from '../../../../components/tabs/TabsList.svelte'
  import TabsTrigger from '../../../../components/tabs/TabsTrigger.svelte'
  import TabsContent from '../../../../components/tabs/TabsContent.svelte'
  import PrintersTabContent from './PrintersTabContent.svelte'
  import GroupsTabContent from './GroupsTabContent.svelte'
  import EmptyProjectModal from '../../../../components/empty-project-modal/EmptyProjectModal.svelte'
  import i18n from '../../../../i18n'
  import { getContext } from 'svelte'
  import UpdateProjectName from '../../buttons/UpdateProjectName.svelte'
  import usePrintersQueries from '../../hooks/usePrintersQueries'
  import useGroupsQueries from '../../hooks/useGroupsQueries'
  import useUploadedFilesQueries from '../../hooks/useUploadedFilesQueries'

  type Props = {
    sceneLoaded?: boolean
  }

  const { sceneLoaded = false }: Props = $props()

  const projectContext = getContext<ProjectContext>('projectContext')

  const { usePrinters, addPrinterMutation } = usePrintersQueries()
  const printers = usePrinters()

  const { useGroups } = useGroupsQueries()
  const groups = useGroups()

  const { uploadFileMutation } = useUploadedFilesQueries()

  let isEmptyProjectModalOpen = $state(false)
  let wasEmpty = false

  // Re-opens every time the project transitions into "empty" (nothing right after
  // load, or everything removed later) but never fights a manual close while the
  // empty state hasn't actually changed.
  $effect(() => {
    if (!sceneLoaded || !printers.isSuccess || !groups.isSuccess) return

    const isEmpty = printers.data.length === 0 && groups.data.length === 0
    if (isEmpty && !wasEmpty) isEmptyProjectModalOpen = true
    wasEmpty = isEmpty
  })
</script>

<aside class="container">
  {#if projectContext.project}
    <UpdateProjectName project={projectContext.project} />
    <div class="content">
      <Tabs class="tabs" defaultValue="printers">
        <TabsList class="list">
          <TabsTrigger value="printers">
            {$i18n.t('project.leftBar.printers.title')}
          </TabsTrigger>
          <TabsTrigger value="groups">
            {$i18n.t('project.leftBar.groups.title')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="printers">
          <PrintersTabContent
            onRequestAddPrinter={() => (isEmptyProjectModalOpen = true)}
          />
        </TabsContent>
        <TabsContent value="groups">
          <GroupsTabContent />
        </TabsContent>
      </Tabs>
    </div>
  {/if}
</aside>

<EmptyProjectModal
  bind:open={isEmptyProjectModalOpen}
  onAddPrinter={model => addPrinterMutation.mutate(model)}
  onImportFile={files => uploadFileMutation.mutate(files)}
/>

<style lang="scss">
  .container {
    align-items: stretch;
    border-right: 1px solid var(--grid-stroke);
    display: flex;
    flex-direction: column;
    flex: 0 0 17rem;

    .content {
      display: flex;
      flex-direction: column;
      flex: 1;
      overflow: hidden;
      border-top: 1px solid var(--grid-stroke);

      :global(.tabs) {
        display: flex;
        flex-direction: column;
        flex: 1;
        overflow: hidden;
      }

      :global(.list) {
        padding: 1rem;
        border-bottom: 1px solid var(--grid-stroke);
      }
    }
  }
</style>
